import { useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { rechercherEntreprise, type EntrepriseTrouvee } from '../services/api';
import CarteEntreprise from './CarteEntreprise';

interface Props {
  value: string;
  onChange: (valeur: string) => void;
  /** Appelé quand le répertoire reconnaît le numéro saisi. */
  onEntreprise: (entreprise: EntrepriseTrouvee) => void;
  /** Fourni, un bouton propose de recopier la raison sociale officielle. */
  onReprendreNom?: (nom: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Champ SIREN / SIRET qui va chercher les coordonnées de l'entreprise.
 *
 * La saisie n'est jamais bloquée : le champ reste facultatif, et une
 * recherche infructueuse — numéro inconnu, répertoire indisponible — se
 * contente de ne rien pré-remplir.
 */
export default function ChampSiret({
  value,
  onChange,
  onEntreprise,
  onReprendreNom,
  placeholder = 'SIRET ou SIREN (optionnel)',
  className = 'input',
}: Props) {
  const [etat, setEtat] = useState<'repos' | 'recherche' | 'trouve' | 'inconnu'>('repos');
  const [trouvee, setTrouvee] = useState<EntrepriseTrouvee | null>(null);

  // Le dernier numéro interrogé : évite de relancer la recherche à chaque
  // frappe une fois les 14 chiffres atteints.
  const dernierNumero = useRef<string | null>(null);

  // La réponse arrive après coup : sans ce garde-fou, une recherche annulée
  // par une nouvelle saisie écraserait la plus récente.
  const dernierAppel = useRef(0);

  useEffect(() => {
    const chiffres = value.replace(/\D/g, '');

    if (chiffres.length !== 9 && chiffres.length !== 14) {
      dernierNumero.current = null;
      setEtat('repos');
      setTrouvee(null);
      return;
    }

    if (chiffres === dernierNumero.current) return;
    dernierNumero.current = chiffres;

    const appel = ++dernierAppel.current;
    setEtat('recherche');

    // Le numéro se termine souvent au clavier : on laisse la frappe se poser.
    const minuteur = window.setTimeout(async () => {
      const entreprise = await rechercherEntreprise(chiffres);
      if (appel !== dernierAppel.current) return;

      if (entreprise === null) {
        setEtat('inconnu');
        setTrouvee(null);
        return;
      }

      setEtat('trouve');
      setTrouvee(entreprise);
      onEntreprise(entreprise);
    }, 400);

    return () => window.clearTimeout(minuteur);
    // onEntreprise change à chaque rendu du parent : le déclencheur utile est
    // la valeur saisie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="space-y-1">
      <div className="relative">
        <input
          className={className}
          placeholder={placeholder}
          value={value}
          inputMode="numeric"
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
        />
        {etat === 'recherche' && (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
        )}
        {etat === 'trouve' && (
          <Check className="w-4 h-4 text-green-600 absolute right-3 top-1/2 -translate-y-1/2" />
        )}
      </div>

      {etat === 'trouve' && trouvee && (
        <CarteEntreprise entreprise={trouvee} onReprendreNom={onReprendreNom} />
      )}
      {etat === 'inconnu' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Numéro non trouvé au répertoire. Vous pouvez saisir les coordonnées à la main.
        </p>
      )}
    </div>
  );
}
